import React from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaShieldAlt, FaPrint, FaChevronUp, FaLink } from "react-icons/fa";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: d },
});

const sections = [
  { id: "overview", label: "Overview" },
  { id: "info-we-collect", label: "Information We Collect" },
  { id: "how-we-use", label: "How We Use Information" },
  { id: "sharing", label: "How We Share Information" },
  { id: "your-choices", label: "Your Choices" },
  { id: "state-rights", label: "State & Regional Rights" },
  { id: "security-retention", label: "Security & Retention" },
  { id: "children", label: "Children’s Privacy" },
  { id: "changes", label: "Changes" },
  { id: "contact", label: "Contact Us" },
];

export default function Privacy() {
  const onTocClick = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const anchor = (id) => (
    <a
      href={`#${id}`}
      onClick={(e) => {
        e.preventDefault();
        onTocClick(id);
      }}
      className="ml-2 inline-flex items-center text-xs text-[#002b5c]/50 hover:text-[#002b5c]"
      aria-label="Copy link to section"
      title="Copy link to section"
    >
      <FaLink />
    </a>
  );

  return (
    <div className="bg-white text-[#002b5c]">
      <Helmet>
        <title>Privacy Policy | Your Choice Associates</title>
        <meta
          name="description"
          content="Your Choice Associates Privacy Policy — what we collect, how we use it, your rights, and how to contact us."
        />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src="/images/hero.png" alt="" className="w-full h-[280px] md:h-[340px] object-cover" />
  <div className="absolute inset-0 bg-gradient-to-b from-[#002b5c]/90 to-[#002b5c]/75" />

        <div className="absolute inset-0 flex items-end pb-8">
          <div className="w-full px-6">
            <div className="max-w-6xl mx-auto text-white">
              <motion.div {...fade(0)} className="flex items-center gap-3 text-white/90">
                <span className="text-xs uppercase tracking-wider">Legal</span>
                <span className="text-white/40">•</span>
                <span className="text-xs">Effective: <strong>August 14, 2025</strong></span>
              </motion.div>
              <motion.h1 {...fade(0.08)} className="text-3xl md:text-5xl font-extrabold mt-2">
                Privacy Policy
              </motion.h1>
              <motion.p {...fade(0.16)} className="mt-3 max-w-3xl text-white/90">
                How we collect, use, and protect your information across our website and services.
              </motion.p>
              <motion.div {...fade(0.24)} className="mt-5 flex items-center gap-3">
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-sm"
                >
                  <FaPrint /> Print
                </button>
                <span className="inline-flex items-center gap-2 text-xs bg-white/10 px-3 py-1.5 rounded-full">
                  <FaShieldAlt /> Your data. Protected.
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="px-6 py-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-10">
          {/* TOC (sticky on desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 p-4 bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm">
              <p className="text-sm font-semibold mb-3">On this page</p>
              <nav className="space-y-2">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => onTocClick(s.id)}
                    className="block w-full text-left text-sm text-[#002b5c]/80 hover:text-[#001b35]"
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <div>
            {/* Breadcrumb */}
            <div className="text-sm text-[#002b5c]/70 mb-6">
              <Link to="/" className="hover:underline">Home</Link> <span className="mx-1">/</span> Privacy Policy
            </div>

            <section id="overview" className="mb-8">
              <motion.div {...fade(0)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Overview {anchor("overview")}
                </h2>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  This Policy explains how <strong>Your Choice Associates</strong> (“YCA,” “we,” “us”) collects, uses,
                  discloses, and protects information when you visit{" "}
                  <a href="https://yourchoiceassociates.org" className="underline">yourchoiceassociates.org</a>
                  (“Site”), submit forms (including{" "}
                  <Link className="underline" to="/contact">Contact</Link> and{" "}
                  <Link className="underline" to="/partner-apply">Partner Apply</Link>), or interact with us.
                  By using the Site, you agree to this Policy.
                </p>

                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
                  <strong>Note:</strong> The Questionnaire page is a gateway intake form. We do <em>not</em> collect SMS
                  consent there and do not send SMS based on that form alone.
                </div>
              </motion.div>
            </section>

            <section id="info-we-collect" className="mb-8">
              <motion.div {...fade(0.04)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Information We Collect {anchor("info-we-collect")}
                </h2>
                <ul className="mt-4 list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Contact details:</strong> name, email, phone, message (Contact page may include SMS consent).</li>
                  <li><strong>Gateway intake:</strong> needs, location, preferences (no SMS consent collected on Questionnaire).</li>
                  <li><strong>Broker onboarding:</strong> licensing/NPN, appointed states, E&O, profile, business contact.</li>
                  <li><strong>Technical data:</strong> IP/device, pages viewed, interactions (security & performance).</li>
                  <li><strong>Cookies & similar tech:</strong> session management, analytics, and security.</li>
                </ul>
              </motion.div>
            </section>

            <section id="how-we-use" className="mb-8">
              <motion.div {...fade(0.08)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  How We Use Information {anchor("how-we-use")}
                </h2>
                <ul className="mt-4 list-disc pl-6 space-y-2 text-gray-700">
                  <li>Respond to requests and connect consumers with licensed brokers.</li>
                  <li>Send confirmations, follow-ups, and appointment reminders when consented on Contact.</li>
                  <li>Provide lead alerts and account notices to consenting brokers.</li>
                  <li>Operate, secure, and improve the Site and services; comply with law.</li>
                </ul>
              </motion.div>
            </section>

            <section id="sharing" className="mb-8">
              <motion.div {...fade(0.12)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  How We Share Information {anchor("sharing")}
                </h2>
                <ul className="mt-4 list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Licensed brokers</strong> to facilitate assistance (independent compliance).</li>
                  <li><strong>Service providers</strong> (e.g., Firebase, Resend, Twilio, analytics, security).</li>
                  <li><strong>Legal/compliance</strong> when required or to protect rights and safety.</li>
                  <li><strong>Business transfers</strong> in a merger or similar event.</li>
                </ul>
              </motion.div>
            </section>

            <section id="your-choices" className="mb-8">
              <motion.div {...fade(0.16)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Your Choices {anchor("your-choices")}
                </h2>
                <ul className="mt-4 list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>SMS:</strong> Consent collected on{" "}
                    <Link to="/contact" className="underline">/contact</Link> and for brokers on{" "}
                    <Link to="/partner-apply" className="underline">/partner-apply</Link>.
                    Reply <strong>STOP</strong> to cancel, <strong>HELP</strong> for help.
                    See <Link to="/sms-terms" className="underline">SMS Terms</Link>.
                  </li>
                  <li><strong>Email:</strong> unsubscribe or contact support to opt out of non-essential email.</li>
                  <li><strong>Cookies:</strong> manage via your browser; some features may be limited.</li>
                  <li>
                    <strong>Access/Deletion:</strong>{" "}
                    <a className="underline" href="mailto:support@yourchoiceassociates.org">
                      support@yourchoiceassociates.org
                    </a>.
                  </li>
                </ul>
              </motion.div>
            </section>

            <section id="state-rights" className="mb-8">
              <motion.div {...fade(0.2)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  State & Regional Rights {anchor("state-rights")}
                </h2>
                <p className="mt-4 text-gray-700">
                  Depending on your location (e.g., CA, CO, CT, VA, EEA/UK), you may have additional rights (access,
                  correction, deletion, portability, restriction/objection). Contact{" "}
                  <a className="underline" href="mailto:privacy@yourchoiceassociates.org">
                    privacy@yourchoiceassociates.org
                  </a>.
                </p>
                <p className="mt-3 text-sm text-gray-600">
                  We do <strong>not sell</strong> personal information and do not target ads using sensitive categories.
                </p>
              </motion.div>
            </section>

            <section id="security-retention" className="mb-8">
              <motion.div {...fade(0.24)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Security & Retention {anchor("security-retention")}
                </h2>
                <p className="mt-4 text-gray-700">
                  We apply administrative, technical, and physical safeguards. No method is 100% secure. We retain data
                  only as long as needed for services, legal obligations, and legitimate business purposes.
                </p>
              </motion.div>
            </section>

            <section id="children" className="mb-8">
              <motion.div {...fade(0.28)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Children’s Privacy {anchor("children")}
                </h2>
                <p className="mt-4 text-gray-700">
                  Our services are not directed to children under 13. If we learn we collected information from a child,
                  we will delete it.
                </p>
              </motion.div>
            </section>

            <section id="changes" className="mb-8">
              <motion.div {...fade(0.32)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Changes {anchor("changes")}
                </h2>
                <p className="mt-4 text-gray-700">
                  We may update this Policy. The effective date above reflects the latest revision. Material changes will
                  be posted on this page.
                </p>
              </motion.div>
            </section>

            <section id="contact" className="mb-4">
              <motion.div {...fade(0.36)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Contact Us {anchor("contact")}
                </h2>
                <p className="mt-4 text-gray-700">
                  Email{" "}
                  <a href="mailto:support@yourchoiceassociates.org" className="underline">
                    support@yourchoiceassociates.org
                  </a>{" "}
                  or call (917) 902-4471.
                </p>
                <p className="mt-3 text-xs text-gray-500">
                  Medicare disclaimer: We are not connected with or endorsed by the U.S. government or the federal Medicare program.
                </p>
              </motion.div>
            </section>

            {/* Back to top */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="fixed bottom-6 right-6 z-10 inline-flex items-center gap-2 rounded-full shadow-lg bg-[#002b5c] text-white px-4 py-3 hover:bg-[#001b35]"
              aria-label="Back to top"
            >
              <FaChevronUp /> Top
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
